import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import schoolLogo from "../assets/rsps-logo2.png";
import { buildReceiptTotalInWords } from "./receiptPdf";
import {
  buildNewReceiptQrValue,
  generateTransactionDocumentId,
} from "./transactionId";

const SCHOOL_NAME = "Rising Stars Public School";
const SCHOOL_ADDRESS = "Rising Stars Public School, India";
// data.paymentHistory is filtered down to only the latest payment date's
// transactions (see getLatestDayHistory), so this is just a safety cap in
// case one day has an unusually large number of payments.
const MAX_HISTORY_ROWS = 10;

function toNumber(value) {
  const parsed = Number(
    String(value ?? "")
      .replace(/,/g, "")
      .trim(),
  );
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatNumber(value) {
  return toNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

function hasNumericValue(value) {
  if (value === null || value === undefined || value === "") return false;
  return Number.isFinite(Number(String(value).replace(/,/g, "").trim()));
}

function formatCurrency(value) {
  if (!hasNumericValue(value)) return String(value || "-").trim() || "-";
  return `Rs. ${formatNumber(value)}`;
}

function normaliseDate(value) {
  if (!value) return new Date().toISOString().slice(0, 10);
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [day, month, year] = value.split("/");
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }
  }
  if (typeof value === "object" && value?.seconds) {
    return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  }
  return new Date().toISOString().slice(0, 10);
}

function formatDate(value) {
  return normaliseDate(value);
}

function loadImageAsDataUrl(src, options = {}) {
  const { opacity = 1 } = options;
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Unable to draw receipt image."));
        return;
      }
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.globalAlpha = Math.max(0, Math.min(1, opacity));
      context.drawImage(image, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = () => reject(new Error("Unable to load receipt image."));
    image.src = src;
  });
}

function getPaymentHistoryTotal(item) {
  return (
    toNumber(item?.feePaymentAmount) +
    toNumber(item?.transportPaymentAmount) +
    toNumber(item?.arrearPaymentAmount) +
    toNumber(item?.miscPaymentAmount)
  );
}

function normaliseHistoryItem(item, index) {
  const paymentDate = item?.paymentDate || item?.date || item?.recordedAt;
  const transactionDocumentId = String(
    item?.transactionDocumentId ||
      item?.documentTransactionId ||
      item?.transactionId ||
      "",
  ).trim();
  return {
    index: index + 1,
    paymentDate: paymentDate || new Date().toISOString(),
    paymentMode: String(item?.paymentMode || "-").trim(),
    transactionId: String(item?.transactionId || "-").trim(),
    transactionDocumentId:
      transactionDocumentId || String(item?.transactionId || "-").trim(),
    feePaymentAmount: toNumber(item?.feePaymentAmount),
    transportPaymentAmount: toNumber(item?.transportPaymentAmount),
    arrearPaymentAmount: toNumber(item?.arrearPaymentAmount),
    miscPaymentAmount: toNumber(item?.miscPaymentAmount),
    miscPaymentRemark: String(item?.miscPaymentRemark || "").trim(),
    remarks: String(item?.remarks || "").trim(),
    recordedAt: item?.recordedAt || null,
  };
}

function collectHistoryItems(receipt = {}) {
  const history = Array.isArray(receipt.paymentHistory)
    ? [...receipt.paymentHistory]
    : [];
  if (receipt.currentTransaction) {
    history.unshift(receipt.currentTransaction);
  }

  const normalized = history.map(normaliseHistoryItem);
  const seen = new Set();

  return normalized
    .sort((a, b) => {
      const aTime = new Date(a.recordedAt || a.paymentDate || 0).getTime();
      const bTime = new Date(b.recordedAt || b.paymentDate || 0).getTime();
      return bTime - aTime;
    })
    .filter((item) => {
      const key = JSON.stringify([
        item.transactionDocumentId,
        normaliseDate(item.paymentDate),
        item.feePaymentAmount,
        item.transportPaymentAmount,
        item.arrearPaymentAmount,
        item.miscPaymentAmount,
      ]);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function safeText(value, fallback = "-") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function getGeneratedByLabel(receipt) {
  const paymentHistory = Array.isArray(receipt?.paymentHistory)
    ? receipt.paymentHistory
    : [];
  const user = paymentHistory.length
    ? paymentHistory[paymentHistory.length - 1]?.user
    : null;
  const displayName = safeText(
    user?.displayName || user?.fullName || user?.name || receipt?.generatedBy,
    "",
  );
  if (displayName) return displayName;

  const uid = safeText(
    user?.uid || receipt?.createdBy || receipt?.generatedByUid,
    "",
  );
  return uid || "N/A";
}

function roundAmount(value) {
  return Math.round(toNumber(value));
}

function allocateAmountAcrossBases(bases, amount) {
  const normalizedBases = bases.map((base) => Math.max(0, roundAmount(base)));
  const totalBase = normalizedBases.reduce((sum, base) => sum + base, 0);
  const totalAmount = Math.max(0, roundAmount(amount));

  if (totalBase <= 0 || totalAmount <= 0) {
    return normalizedBases.map(() => 0);
  }

  const shares = normalizedBases.map(() => 0);
  let allocated = 0;

  normalizedBases.forEach((base, index) => {
    if (base <= 0) return;
    if (index === normalizedBases.length - 1) {
      shares[index] = Math.max(0, totalAmount - allocated);
      return;
    }

    const share = Math.min(base, Math.floor((totalAmount * base) / totalBase));
    shares[index] = share;
    allocated += share;
  });

  return shares;
}

function buildChargedRow(description, due, category) {
  return {
    description,
    due: roundAmount(due),
    paid: 0,
    remaining: 0,
    category,
  };
}

function getReceiptNumber(receipt, ...keys) {
  const sources = [receipt, receipt?.paymentDetails, receipt?.feeItems];
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    for (const key of keys) {
      const value = source[key];
      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        return toNumber(value);
      }
    }
  }
  return 0;
}

function sumHistoryPayments(receipt, key) {
  const history = Array.isArray(receipt?.paymentHistory)
    ? receipt.paymentHistory
    : [];
  return history.reduce((sum, item) => sum + toNumber(item?.[key]), 0);
}

function buildYearlyFeeRows(receipt = {}) {
  const monthlyFeeMonths =
    toNumber(receipt.monthlyFeeMonths) ||
    toNumber(receipt?.paymentDetails?.monthlyFeeMonths) ||
    12;
  const transportMonths =
    toNumber(receipt.transportMonths) ||
    toNumber(receipt?.paymentDetails?.transportMonths) ||
    11;

  const monthlyTuitionFee = getReceiptNumber(receipt, "monthlyTuitionFee");
  const monthlyComputerFee = getReceiptNumber(receipt, "monthlyComputerFee");
  const monthlySportsFee = getReceiptNumber(receipt, "monthlySportsFee");
  const monthlyMaintenanceFee = getReceiptNumber(
    receipt,
    "monthlyMaintenanceFee",
  );
  const monthlyAnnual = getReceiptNumber(receipt, "monthlyAnnual");
  const monthlyFeesTotal = getReceiptNumber(receipt, "monthlyFees");
  const otherAnnualCharge = getReceiptNumber(receipt, "otherAnnualCharge");
  const otherDiaryCharge = getReceiptNumber(receipt, "otherDiaryCharge");
  const otherIdCharge = getReceiptNumber(receipt, "otherIdCharge");
  const otherExamFee = getReceiptNumber(receipt, "otherExamFee");
  const otherTotal = getReceiptNumber(receipt, "otherTotal");
  const customChargesTotal = getReceiptNumber(receipt, "customChargesTotal");
  const transportFeePerMonth = getReceiptNumber(
    receipt,
    "transportFeePerMonth",
  );
  const transportAnnual = getReceiptNumber(receipt, "transportAnnual");
  const transportFees = getReceiptNumber(receipt, "transportFees");
  const feePaidAmount =
    getReceiptNumber(
      receipt,
      "feePaidAmount",
      "feePaid",
      "totalFeePaidAmount",
    ) || sumHistoryPayments(receipt, "feePaymentAmount");
  const transportPaidAmount =
    getReceiptNumber(
      receipt,
      "transportPaidAmount",
      "transportPaid",
      "totalTransportPaidAmount",
    ) || sumHistoryPayments(receipt, "transportPaymentAmount");
  const arrearPaidAmount =
    getReceiptNumber(
      receipt,
      "arrearPaidAmount",
      "arrearPaid",
      "totalArrearPaidAmount",
    ) || sumHistoryPayments(receipt, "arrearPaymentAmount");
  const miscPaymentAmount =
    getReceiptNumber(receipt, "miscPaymentAmount", "totalMiscPaidAmount") ||
    sumHistoryPayments(receipt, "miscPaymentAmount");
  const prevArrear = getReceiptNumber(
    receipt,
    "prevArrear",
    "previousYearArrears",
    "previousDue",
  );
  const arrearPaid = getReceiptNumber(
    receipt,
    "arrearPaid",
    "arrearPaidAmount",
  );
  const discountAmount = Math.max(
    0,
    roundAmount(
      getReceiptNumber(receipt, "discountAmount") +
        getReceiptNumber(receipt, "scholarshipAmount"),
    ),
  );

  const monthlyBreakdown = [
    {
      description: "Tuition Fee",
      due: monthlyTuitionFee * monthlyFeeMonths,
      category: "fee",
    },
    {
      description: "Computer Fee",
      due: monthlyComputerFee * monthlyFeeMonths,
      category: "fee",
    },
    {
      description: "Sports Fee",
      due: monthlySportsFee * monthlyFeeMonths,
      category: "fee",
    },
    {
      description: "Maintenance Fee",
      due: monthlyMaintenanceFee * monthlyFeeMonths,
      category: "fee",
    },
  ].filter((item) => item.due > 0);

  const feeRows = monthlyBreakdown.length
    ? monthlyBreakdown
    : monthlyAnnual > 0
      ? [buildChargedRow("Monthly Fees", monthlyAnnual, "fee")]
      : monthlyFeesTotal > 0
        ? [buildChargedRow("Monthly Fees", monthlyFeesTotal, "fee")]
        : [];

  const annualBreakdown = [
    {
      description: "Annual Charges",
      due: otherAnnualCharge,
      category: "fee",
    },
    {
      description: "Diary Charge",
      due: otherDiaryCharge,
      category: "fee",
    },
    {
      description: "ID Card Fee",
      due: otherIdCharge,
      category: "fee",
    },
    {
      description: "Examination Fee",
      due: otherExamFee,
      category: "fee",
    },
  ].filter((item) => item.due > 0);

  if (annualBreakdown.length > 0) {
    feeRows.push(...annualBreakdown);
  } else if (otherTotal > 0) {
    feeRows.push(buildChargedRow("Annual Charges", otherTotal, "fee"));
  }

  const customCharges = Array.isArray(receipt.customCharges)
    ? receipt.customCharges
    : Array.isArray(receipt?.paymentDetails?.customCharges)
      ? receipt.paymentDetails.customCharges
      : [];
  if (customCharges.length > 0) {
    customCharges.forEach((charge, index) => {
      const amount = toNumber(charge?.amount);
      if (amount <= 0) return;
      feeRows.push({
        description: safeText(
          charge?.name ||
            charge?.label ||
            charge?.description ||
            `Custom Charge ${index + 1}`,
        ),
        due: roundAmount(amount),
        paid: 0,
        remaining: 0,
        category: "fee",
      });
    });
  } else if (customChargesTotal > 0) {
    feeRows.push(buildChargedRow("Custom Charges", customChargesTotal, "fee"));
  }

  const transportDue =
    transportFeePerMonth > 0
      ? transportFeePerMonth * transportMonths
      : transportAnnual > 0
        ? transportAnnual
        : transportFees;
  if (transportDue > 0) {
    feeRows.push({
      description: "Transport Fee",
      due: roundAmount(transportDue),
      paid: 0,
      remaining: 0,
      category: "transport",
    });
  }

  const arrearDueRaw = prevArrear;
  const arrearDue = Math.max(
    roundAmount(arrearDueRaw),
    roundAmount(arrearPaid),
  );
  if (arrearDue > 0 || arrearPaid > 0) {
    feeRows.push({
      description: "Previous Year Arrear",
      due: arrearDue,
      paid: roundAmount(arrearPaid),
      remaining: 0,
      category: "arrear",
    });
  }

  const feePoolRows = feeRows.filter((row) => row.category === "fee");
  const feeBases = feePoolRows.map((row) => row.due);
  const discountShares = allocateAmountAcrossBases(feeBases, discountAmount);
  const feeRowsAfterDiscount = feePoolRows.map((row, index) => ({
    ...row,
    due: Math.max(0, row.due - discountShares[index]),
  }));

  const paidAmount = toNumber(feePaidAmount);
  const paidShares = allocateAmountAcrossBases(
    feeRowsAfterDiscount.map((row) => row.due),
    paidAmount,
  );

  const normalizedFeeRows = feeRowsAfterDiscount.map((row, index) => {
    const paid = paidShares[index];
    return {
      ...row,
      paid,
      remaining: Math.max(0, row.due - paid),
    };
  });

  const transportRow = feeRows.find((row) => row.category === "transport");
  if (transportRow) {
    transportRow.paid = roundAmount(transportPaidAmount);
    transportRow.remaining = Math.max(0, transportRow.due - transportRow.paid);
  }

  const arrearRow = feeRows.find((row) => row.category === "arrear");
  if (arrearRow) {
    arrearRow.paid = roundAmount(arrearPaidAmount);
    arrearRow.remaining = Math.max(0, arrearRow.due - arrearRow.paid);
  }

  if (miscPaymentAmount > 0) {
    feeRows.push({
      description: "Miscellaneous Charges",
      due: roundAmount(miscPaymentAmount),
      paid: roundAmount(miscPaymentAmount),
      remaining: 0,
      category: "misc",
    });
  }

  const rows = [...normalizedFeeRows];
  if (transportRow) rows.push(transportRow);
  if (arrearRow) rows.push(arrearRow);
  if (miscPaymentAmount > 0) {
    rows.push({
      description: "Miscellaneous Charges",
      due: roundAmount(miscPaymentAmount),
      paid: roundAmount(miscPaymentAmount),
      remaining: 0,
      category: "misc",
    });
  }

  const totalDue = rows.reduce((sum, row) => sum + row.due, 0);
  const totalPaid = rows.reduce((sum, row) => sum + row.paid, 0);
  const totalRemaining = rows.reduce((sum, row) => sum + row.remaining, 0);

  if (totalDue <= 0 && totalPaid <= 0 && totalRemaining <= 0) {
    const fallbackFeeRow =
      monthlyAnnual > 0
        ? buildChargedRow("Monthly Fees", monthlyAnnual, "fee")
        : monthlyFeesTotal > 0
          ? buildChargedRow("Monthly Fees", monthlyFeesTotal, "fee")
          : null;
    const fallbackRows = fallbackFeeRow ? [fallbackFeeRow] : [];
    if (otherTotal > 0) {
      fallbackRows.push(buildChargedRow("Annual Charges", otherTotal, "fee"));
    }
    if (transportAnnual > 0 || transportFees > 0) {
      fallbackRows.push(
        buildChargedRow(
          "Transport Fee",
          transportAnnual > 0 ? transportAnnual : transportFees,
          "transport",
        ),
      );
    }
    if (prevArrear > 0 || arrearPaid > 0) {
      fallbackRows.push({
        description: "Previous Year Arrear",
        due: Math.max(prevArrear, arrearPaid),
        paid: arrearPaid,
        remaining: Math.max(0, Math.max(prevArrear, arrearPaid) - arrearPaid),
        category: "arrear",
      });
    }
    return {
      rows: fallbackRows.map((row) => ({
        ...row,
        paid: row.paid || 0,
        remaining: row.remaining || 0,
      })),
      discountAmount,
      totalDue: fallbackRows.reduce((sum, row) => sum + row.due, 0),
      totalPaid: fallbackRows.reduce((sum, row) => sum + (row.paid || 0), 0),
      totalRemaining: fallbackRows.reduce(
        (sum, row) => sum + (row.remaining || 0),
        0,
      ),
    };
  }

  return {
    rows,
    discountAmount,
    totalDue,
    totalPaid,
    totalRemaining,
  };
}

function drawBorderedInfoSection(doc, x, y, title, rows, options = {}) {
  const {
    boxWidth = 92,
    boxHeight = 44,
    labelX = x + 2,
    valueX = x + 30,
    rowStep = 6,
  } = options;

  doc.setDrawColor(205);
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(x, y, boxWidth, boxHeight, 2, 2, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text(title, x + 2, y + 5);

  let currentY = y + 12;
  rows.forEach(([label, value, formatHint], index) => {
    const valueText = String(value || "-");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.4);
    doc.text(`${label} :`, labelX, currentY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.2);
    doc.text(valueText, valueX, currentY);

    if (formatHint) {
      const valueWidth = doc.getTextWidth(valueText);
      const hintText = ` (${formatHint})`;
      const hintX = valueX + valueWidth + 1.5;
      const availableRight = x + boxWidth - 3;
      if (hintX + doc.getTextWidth(hintText) <= availableRight) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.4);
        doc.setTextColor(120);
        doc.text(hintText, hintX, currentY);
        doc.setTextColor(0);
      }
    }

    currentY += rowStep;
  });

  return y + boxHeight;
}

function getLatestDayHistory(paymentHistory) {
  if (!paymentHistory.length) return [];
  // paymentHistory is already sorted newest-first, so the first item's
  // date is the latest date. Keep only the items that share that date,
  // and drop anything from an earlier date (e.g. 23rd July).
  const latestDate = normaliseDate(paymentHistory[0].paymentDate);
  return paymentHistory.filter(
    (item) => normaliseDate(item.paymentDate) === latestDate,
  );
}

export function buildNewReceiptPayload(receipt = {}) {
  const studentId = safeText(
    receipt.admissionNo || receipt.admissionNumber || receipt.uid,
  );
  const receiptNo = safeText(receipt.receiptNo || receipt.transactionNo || "-");
  const allPaymentHistory = collectHistoryItems(receipt);
  const latestHistory = allPaymentHistory[0] || null;
  // Only keep transactions made on the most recent payment date. E.g. if
  // there are 2 payments on 25th July, both are kept and totalled; any
  // older payment (e.g. 23rd July) is excluded entirely.
  const paymentHistory = getLatestDayHistory(allPaymentHistory);
  const latestHistoryTransactionId = safeText(
    latestHistory?.transactionDocumentId ||
      latestHistory?.documentTransactionId ||
      latestHistory?.transactionId,
  );
  const transactionDocumentId = safeText(
    latestHistoryTransactionId ||
      receipt.transactionDocumentId ||
      receipt.documentTransactionId ||
      generateTransactionDocumentId({
        paymentDate: receipt.paymentDate || latestHistory?.paymentDate,
      }),
  );
  const totalPaidAmount = paymentHistory.reduce(
    (sum, item) => sum + getPaymentHistoryTotal(item),
    0,
  );
  const totalAmount = toNumber(receipt.totalAmount);
  const paymentDate = normaliseDate(
    receipt.paymentDate || latestHistory?.paymentDate || receipt.date,
  );

  return {
    schoolName: safeText(receipt.schoolName, SCHOOL_NAME),
    schoolAddress: safeText(receipt.schoolAddress, SCHOOL_ADDRESS),
    logo: receipt.logo || schoolLogo,
    receiptNo,
    date: formatDate(receipt.date || paymentDate),
    admissionNo: studentId,
    studentName: safeText(
      receipt.studentName || receipt.name || receipt.childName,
    ),
    session: safeText(receipt.session || receipt.academicYear),
    className: safeText(
      receipt.className || receipt.class || receipt.classLabel,
    ),
    paymentMode: safeText(receipt.paymentMode),
    bank: safeText(receipt.bank),
    transactionNo: safeText(receipt.transactionNo || transactionDocumentId),
    transactionId: transactionDocumentId,
    transactionDocumentId,
    paymentDate: formatDate(paymentDate),
    counterNo: "ONLINE",
    totalAmount,
    totalPaidAmount,
    totalInWords: buildReceiptTotalInWords(totalAmount),
    totalPaidAmountInWords: buildReceiptTotalInWords(totalPaidAmount),
    feeItems: Array.isArray(receipt.feeItems) ? receipt.feeItems : [],
    paymentHistory,
    qrValue: buildNewReceiptQrValue({
      receiptNo,
      studentId,
      transactionId: transactionDocumentId,
      paymentDate,
    }),
    paymentDetails: receipt.paymentDetails || {},
    footerNote:
      receipt.footerNote ||
      "This is a computer-generated receipt. Signature not required.",
  };
}

export async function buildNewReceiptPdf(receipt = {}) {
  const data = buildNewReceiptPayload(receipt);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const right = pageWidth - 10;

  const [logoDataUrl, qrDataUrl] = await Promise.all([
    loadImageAsDataUrl(data.logo),
    QRCode.toDataURL(String(data.qrValue), {
      margin: 0,
      width: 220,
      errorCorrectionLevel: "M",
      color: { dark: "#000000", light: "#FFFFFF" },
    }),
  ]);

  const watermarkLogo = await loadImageAsDataUrl(data.logo, {
    opacity: 0.08, // 8% opacity
  });
  const watermarkSize = 90;

  doc.addImage(
    watermarkLogo,
    "PNG",
    (pageWidth - watermarkSize) / 2,
    (pageHeight - watermarkSize) / 2,
    watermarkSize,
    watermarkSize,
  );

  doc.setDrawColor(170);
  doc.setLineWidth(0.3);
  doc.rect(8, 8, pageWidth - 16, pageHeight - 16);

  // Logo (30% larger)
  doc.addImage(logoDataUrl, "PNG", 11, 10, 21, 21);

  // School Name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(data.schoolName, pageWidth / 2, 18, { align: "center" });

  // School Address
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.2);
  doc.text(data.schoolAddress, pageWidth / 2, 24, { align: "center" });

  // Receipt Title Background
  doc.setFillColor(232, 236, 255);
  doc.rect(8, 31, pageWidth - 16, 8, "F");

  // Receipt Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("PAYMENT RECEIPT", pageWidth / 2, 36.2, { align: "center" });

  const infoStartY = 43;
  const studentRows = [
    ["Student", data.studentName],
    ["Father Name", receipt.fatherName || "N/A"],
    ["Date Of Birth", formatDate(receipt.dob) || "N/A", "yyyy-mm-dd"],
    ["Class", data.className],
    ["Academic Year", data.session],
  ];
  const paymentRows = [
    ["UID", data.admissionNo],
    ["Date", data.date, "yyyy-mm-dd"],
    ["Transaction ID", data.transactionDocumentId || data.transactionId || "-"],
    ["Counter", data.counterNo],
    ["Generated By", getGeneratedByLabel(receipt)],
  ];
  const studentSectionBottom = drawBorderedInfoSection(
    doc,
    10,
    infoStartY,
    "Student Information",
    studentRows,
    { boxWidth: 92, boxHeight: 44, labelX: 12, valueX: 36, rowStep: 6 },
  );
  const paymentSectionBottom = drawBorderedInfoSection(
    doc,
    108,
    infoStartY,
    "Payment Information",
    paymentRows,
    { boxWidth: 92, boxHeight: 44, labelX: 112, valueX: 142, rowStep: 6 },
  );

  const feeTableStartY =
    Math.max(studentSectionBottom, paymentSectionBottom) + 4;
  const yearlyFeeSummary = buildYearlyFeeRows(receipt);
  const feeRows = yearlyFeeSummary.rows;
  const totalRowIndex = feeRows.length;

  feeRows.push({
    description: "TOTAL",
    due: yearlyFeeSummary.totalDue,
    paid: yearlyFeeSummary.totalPaid,
    remaining: yearlyFeeSummary.totalRemaining,
  });
  autoTable(doc, {
    startY: feeTableStartY,
    head: [["Description", "Due", "Paid", "Remaining"]],

    body: feeRows.map((item) => [
      item.description,
      formatNumber(item.due),
      formatNumber(item.paid),
      formatNumber(item.remaining),
    ]),
    theme: "plain",
    margin: { left: 10, right: 10 },
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: 7.6,
      cellPadding: 1.3,
      valign: "middle",
      lineColor: [170, 170, 170],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [233, 233, 233],
      textColor: 20,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 88 },
      1: { cellWidth: 32, halign: "right" },
      2: { cellWidth: 32, halign: "right" },
      3: { cellWidth: 32, halign: "right" },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === totalRowIndex) {
        data.cell.styles.fillColor = [233, 233, 233];
        data.cell.styles.fontStyle = "bold";
      }
      if (data.section === "head") {
        data.cell.styles.fillColor = [233, 233, 233];
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  const tableBottomY = doc.lastAutoTable?.finalY || feeTableStartY;
  const discountNoteY = tableBottomY + 3;
  if (yearlyFeeSummary.discountAmount > 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.2);
    doc.text(
      `Discount applied: ${formatCurrency(yearlyFeeSummary.discountAmount)}`,
      10,
      discountNoteY,
    );
  }

  const paymentInfoTop =
    discountNoteY + (yearlyFeeSummary.discountAmount > 0 ? 5 : 4);
  doc.setFillColor(244, 246, 255);
  doc.roundedRect(10, paymentInfoTop, 190, 18, 2, 2, "S");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.3);
  doc.text("Total Paid", 14, paymentInfoTop + 6.2);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.text(formatCurrency(data.totalPaidAmount), 14, paymentInfoTop + 13.2);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.3);
  doc.text("Amount in Words", 108, paymentInfoTop + 6.2);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.3);
  const amountInWords = doc.splitTextToSize(data.totalPaidAmountInWords, 88);
  doc.text(amountInWords, 108, paymentInfoTop + 11.2);

  const recentHistory = data.paymentHistory.slice(0, MAX_HISTORY_ROWS);
  const historyStartY = paymentInfoTop + 28;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Previous Transactions", 10, historyStartY - 2);

  autoTable(doc, {
    startY: historyStartY,
    head: [
      [
        "Date",
        "Fee",
        "Transport",
        "Arrear",
        "Misc",
        "Total",
        "Mode",
        "Transaction ID",
      ],
    ],
    body: recentHistory.length
      ? recentHistory.map((item) => [
          formatDate(item.paymentDate),
          formatNumber(item.feePaymentAmount),
          formatNumber(item.transportPaymentAmount),
          formatNumber(item.arrearPaymentAmount),
          formatNumber(item.miscPaymentAmount),
          formatNumber(getPaymentHistoryTotal(item)),
          String(item.paymentMode || "-"),
          String(item.transactionDocumentId || item.transactionId || "-"),
        ])
      : [["-", "-", "-", "-", "-", "-", "-", "-"]],
    theme: "grid",
    margin: { left: 10, right: 10 },
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: 6.5,
      cellPadding: 1.05,
      valign: "middle",
      lineColor: [180, 180, 180],
      lineWidth: 0.2,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [233, 233, 233],
      textColor: 20,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 17, halign: "right" },
      2: { cellWidth: 17, halign: "right" },
      3: { cellWidth: 17, halign: "right" },
      4: { cellWidth: 15, halign: "right" },
      5: { cellWidth: 16, halign: "right" },
      6: { cellWidth: 20 },
      7: { cellWidth: 29 },
    },
  });

  const contentBottomY = doc.lastAutoTable?.finalY || historyStartY;
  const footerTopY = Math.max(contentBottomY + 8, 244);
  const qrY = footerTopY;

  doc.addImage(qrDataUrl, "PNG", 10, qrY, 24, 24);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.2);
  doc.text("Scan for receipt reference", 10, qrY + 28);

  doc.setDrawColor(100);
  doc.setLineWidth(0.25);
  doc.line(148, qrY + 15, right, qrY + 15);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.4);
  doc.text("Authorized Signature", right, qrY + 13.2, { align: "right" });

  doc.setDrawColor(185);
  doc.setLineWidth(0.2);
  doc.line(10, qrY + 31, right, qrY + 31);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.3);
  doc.text(data.footerNote, pageWidth / 2, qrY + 35, { align: "center" });

  const fileName =
    `${data.studentName}_${data.admissionNo}_${data.receiptNo}.pdf`
      .replace(/\s+/g, "_")
      .replace(/[^\w.-]/g, "");

  doc.save(fileName);
}

export async function printNewReceiptPdf(receipt = {}) {
  const blob = await buildNewReceiptPdf(receipt);
  if (typeof window === "undefined" || typeof document === "undefined") {
    return blob;
  }

  const url = URL.createObjectURL(blob);
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.left = "0";
  frame.style.top = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.src = url;

  const cleanup = () => {
    URL.revokeObjectURL(url);
    frame.remove();
  };

  frame.onload = () => {
    const frameWindow = frame.contentWindow;
    if (frameWindow) {
      frameWindow.focus();
      const safeCleanup = () => {
        frameWindow.removeEventListener("afterprint", safeCleanup);
        cleanup();
      };
      frameWindow.addEventListener("afterprint", safeCleanup, { once: true });
      frameWindow.print();
      setTimeout(safeCleanup, 60000);
      return;
    }
    cleanup();
  };

  document.body.appendChild(frame);
  return blob;
}
